import React from 'react';
import { graphql } from 'gatsby';
import styled, { ThemeProvider } from 'styled-components';

import Container from '@/components/Container';
import { mediaQuery } from '@/utils/helpers';
import {
  isDarkTheme,
  getFontColorByBackground,
  getBackgroundColor,
} from '@/utils/StyleUtils';
import Title from '@/components/Title';
import Link, { LinkStyle, LinkType } from '@/components/Link';
import RichText, { RichTextType } from '@/components/Richtext';
import About, { AboutType } from '@/components/About';
import FeaturedLink, { FeaturedLinkData } from '@/components/FeaturedLink';

import mask from '@/images/mask.svg';
import pattern from '@/images/pattern.svg';
import Image, { ImageType } from '@/components/Image';

const TitleContainer = styled.div`
  grid-column: 1;
  word-break: break-word;
  text-wrap: balance;
`;

const Description = styled.div`
  grid-column: 1;
  word-break: break-word;
`;

const CTAWrapper = styled.div`
  grid-column: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  margin-top: 2.4rem;
`;

export const ContentWrapper = styled.div`
  ${mediaQuery.lg(`
    max-width: 500px;
    display: grid;
    grid-template-columns: 45% 55%;
    grid-template-rows: auto;
    max-width: 100%;
  `)};

  ${mediaQuery.xl(`
    grid-template-columns: 40% 60%;
    margin-top: 4rem;
  `)};
`;

type NoImageFrameProps = {
  imageFrame?: boolean;
};

export const ImageContentWrapper = styled.div<NoImageFrameProps>`
  position: relative;
  margin-left: auto;
  min-height: 320px;
  margin-bottom: 24px;
  display: flex;
  justify-content: center;

  ${mediaQuery.lg(`
    display: grid;
    grid-row-start: 1;
    grid-row-end: 7;
    grid-column: 2;
    margin-bottom: 0;
    margin-left: 5rem;
  `)};

  ${({ imageFrame }) =>
    imageFrame
      ? `${mediaQuery.xl('margin-left: auto;')}`
      : `${mediaQuery.xl(`
        margin-top: -3.8rem;
        margin-right: -3.2rem;
      `)}`}
}`;

const TopBannerNoFrameImage = styled(Image)``;
const TopBannerImage = styled(Image)`
  position: relative;
  top: 2rem;
  z-index: 2;
  right: 1rem;
  margin: 0 auto;
  mask: url(${mask});
  mask-size: contain;
  mask-repeat: no-repeat;
  width: 100%;
  height: 100%;
  max-width: 310px;
  height: 360px;

  ${mediaQuery.sm(`
    max-width: 310px;
    height: 350px;
    right: 2rem;
  `)}

  ${mediaQuery.lg(`
      top: 0;
      max-width: 608px;
      height: 0;
      padding-top: 106%;
  `)}

  ${mediaQuery.xl(`
    right: 3rem;
  `)}
`;
const PatternImageWrapper = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  bottom: 0;
  width: 100%;
  max-width: 300px;
  margin-right: -10px;

  ${mediaQuery.sm(`
    margin-right: -20px;
  `)}

  ${mediaQuery.md(`
    margin-right: auto;
  `)}

  ${mediaQuery.lg(`
    max-width: 100%;
    height: 600px;
    margin-top: -3.5rem;
  `)}
`;

const Pattern = styled.img`
  display: block;
  width: 100%;
  max-height: 700px;
`;

type FeaturedLinkWrapperProps = {
  background: string;
};

const FeaturedLinkWrapper = styled.div<FeaturedLinkWrapperProps>`
  grid-column: 1;
  display: block;
  grid-row-end: 7;
  padding: 2.4rem 0 0;
  max-width: 500px;

  ${mediaQuery.lg(`
    padding-bottom:6rem;
  `)}

  >* {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    box-shadow: 0px 0px 3px 0px rgba(0, 0, 0, 0.12), 0px 4px 8px 0px rgba(0, 0, 0, 0.1);
    background-color: ${({ background }) => background || 'white'};

    &:first-of-type
    {
      border-radius: 6px 6px 0px 0px;
    }

    &:last-of-type
    {
      border-radius: 0px 0px 6px 6px;
    }

    &:only-child
    {
      border-radius: 6px;
    }
  }
}`;

const TopBannerImageContainer = styled.div`
  z-index: 2;
  filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.1))
    drop-shadow(0 6px 12px rgba(0, 0, 0, 0.12));
`;

type wrapperProps = {
  color: string;
};

const TopBlockWrapper = styled.div<wrapperProps>`
  padding-bottom: 4.8rem;

  ${mediaQuery.lg(`
    padding-bottom: 0;
    margin-bottom: -2rem;
  `)}

  ${({ color }) =>
    color.includes('gradient')
      ? `background-image: ${color}`
      : `background-color: ${color}`};
`;

export type TopBlockType = RichTextType & {
  contentful_id: string;
  title: string;
  about: AboutType;
  image: ImageType;
  pageColor?: string;
  primaryLink: LinkType;
  secondaryLink: LinkType;
  featuredLinks: Array<FeaturedLinkData>;
  imageFrame: boolean;
};

type Props = TopBlockType;

const TopBlock: React.FC<Props> = (props) => {
  const backgroundColor: string = props.pageColor;
  const dark: boolean = isDarkTheme(backgroundColor);
  const fontColor = getFontColorByBackground(backgroundColor);
  const options: any = { theme: dark ? 'dark' : 'white' };
  const featuredBackgroundColor: string = dark
    ? getBackgroundColor('Blue Dark')
    : getBackgroundColor('White');

  const isTransparent =
    props.image.gatsbyImageData &&
    props.image.gatsbyImageData.backgroundColor &&
    (props.image.gatsbyImageData.backgroundColor === 'white' ||
      props.image.gatsbyImageData.backgroundColor === '#080808');

  return (
    <>
      <TopBlockWrapper color={getBackgroundColor(backgroundColor)}>
        <ThemeProvider theme={{ dark: isDarkTheme(backgroundColor) }}>
          <Container>
            <ContentWrapper>
              {props.about && <About data={props.about} dark={dark} />}
              <TitleContainer>
                <Title
                  size='xxxl'
                  level='h1'
                  margin='0 0 1.2rem'
                  lightColor={dark}
                >
                  {props.title}
                </Title>
              </TitleContainer>
              <ImageContentWrapper imageFrame={props.imageFrame}>
                {props.imageFrame && (
                  <>
                    <TopBannerImageContainer>
                      <TopBannerImage
                        transparent={isTransparent}
                        {...props.image}
                      />
                    </TopBannerImageContainer>
                    <PatternImageWrapper>
                      <Pattern src={pattern} />
                    </PatternImageWrapper>
                  </>
                )}
                {!props.imageFrame && (
                  <>
                    <TopBannerNoFrameImage
                      transparent={isTransparent}
                      {...props.image}
                    />
                  </>
                )}
              </ImageContentWrapper>
              {props.richText && (
                <Description>
                  <RichText
                    data={props.richText}
                    defaultPaths={props.defaultPaths}
                    options={options}
                  />
                </Description>
              )}
              <CTAWrapper>
                <Link {...props.primaryLink} linkStyle={LinkStyle.PRIMARY} />
                <Link
                  {...props.secondaryLink}
                  linkStyle={LinkStyle.SECONDARY}
                />
              </CTAWrapper>
              {props.featuredLinks && (
                <FeaturedLinkWrapper background={featuredBackgroundColor}>
                  {React.Children.toArray(
                    props.featuredLinks.map((featuredLink) => {
                      return FeaturedLink({
                        ...featuredLink,
                        fontColor: fontColor,
                      });
                    }),
                  )}
                </FeaturedLinkWrapper>
              )}
            </ContentWrapper>
          </Container>
        </ThemeProvider>
      </TopBlockWrapper>
    </>
  );
};

export default TopBlock;

export const TopBlockFragmentQuery = graphql`
  fragment TopBlockFragment on ContentfulTopBlock {
    contentful_id
    __typename
    title
    selligentOfferName
    about {
      ...AboutFragment
    }
    richText {
      ...RichTextFragment
    }
    primaryLink {
      ...LinkFragment
    }
    secondaryLink {
      ...LinkFragment
    }
    image {
      gatsbyImageData
      ...ImageFragment
    }
    imageFrame
    featuredLinks {
      ...FeaturedLinkFragment
    }
  }
`;
